// Mapeo FHIR del partograma (051): TODO dato discreto del partograma se guarda como Observation
// propio, agrupado bajo un Observation "panel" (`hasMember`) para reconstruirlo. Van en el mismo
// `LoteTransaccional` (transaction Bundle) del formulario: los miembros llevan `fullUrl` `urn:uuid:` y el
// panel los referencia con esas mismas urn (el servidor las sustituye por los ids reales).
//
// Recursos (nunca uno vacío; sin datos no hay ni panel):
//  - dilatación cervical (cm)      código local; no se halló LOINC (búsqueda en tx.fhir.org sin resultados)
//  - FCF del gráfico (/min)        LOINC 55283-6 "Fetal Heart rate"
//  - plano de Hodge + De Lee       valueCodeableConcept (2 codings: plano y De Lee); variedad de posición = component
//  - dip de FCF (I/II/V/M)         valueCodeableConcept
//  - REM / RAM                     valueBoolean true; la hora va en el component `hora-partograma`
//  - tabla horaria a-p, por columna con dato: hora real, posición materna, TA (LOINC 85354-9 con
//    8480-6/8462-4), pulso materno (LOINC 8867-4), FCF (55283-6), duración y frecuencia de
//    contracciones, localización e intensidad del dolor; una hora real sola también se guarda.
// Ojo lectores: la FCF del gráfico (component `hora-partograma`) y la de la tabla (`columna-partograma`) comparten
// el LOINC 55283-6; todos llevan el mismo `effectiveDateTime` (guardado), así que se ordena por esos components.
//
// Tiempo: el papel solo da la hora RELATIVA del gráfico (0-14 h, medias horas) y una "hora real" escrita
// a mano SIN fecha, así que no hay fecha/hora absoluta fiable. `effectiveDateTime` = momento del guardado
// (`ahora`); la posición en el partograma viaja en el component `hora-partograma` (h, UCUM; puede ser 2.5)
// y, para la tabla, en `columna-partograma` (a-p) + `hora-real` (texto tal cual). No se inventa una fecha.
import type { Encounter, Observation, ObservationComponent, Reference } from '@medplum/fhirtypes';
import { PLANOS_HODGE_PARTOGRAMA, POSICIONES_MATERNAS_PARTOGRAMA, type ColumnaHorariaPartograma, COLUMNAS_HORARIAS_PARTOGRAMA } from '@logiacare/hce-pdf';
import type { LoteTransaccional } from '../transaccionFhir';
import { idTemporal } from '../transaccionFhir';
import type { EstadoPartogramaVisual051 } from './PartogramaVisual';
import type { EstadoTablaHorariaPartograma051 } from './TablaHoraria';

export const SISTEMA_PARTOGRAMA = 'http://logiacare.ec/fhir/partograma';
export const SISTEMA_PARTOGRAMA_PLANO_HODGE = 'http://logiacare.ec/fhir/partograma-plano-hodge';
export const SISTEMA_PARTOGRAMA_DE_LEE = 'http://logiacare.ec/fhir/partograma-de-lee';
export const SISTEMA_PARTOGRAMA_DIP = 'http://logiacare.ec/fhir/partograma-dip';
export const SISTEMA_PARTOGRAMA_POSICION = 'http://logiacare.ec/fhir/partograma-posicion-materna';
export const SISTEMA_PARTOGRAMA_DOLOR_LOCALIZACION = 'http://logiacare.ec/fhir/partograma-dolor-localizacion';
export const SISTEMA_PARTOGRAMA_DOLOR_INTENSIDAD = 'http://logiacare.ec/fhir/partograma-dolor-intensidad';
export const SISTEMA_PARTOGRAMA_PARIDAD = 'http://logiacare.ec/fhir/partograma-paridad';
export const SISTEMA_PARTOGRAMA_MEMBRANAS = 'http://logiacare.ec/fhir/partograma-membranas';
const LOINC = 'http://loinc.org';
const UCUM = 'http://unitsofmeasure.org';

export const CODIGO_PARTOGRAMA = {
  panel: 'partograma',
  dilatacion: 'dilatacion-cervical',
  hodge: 'plano-hodge',
  variedad: 'variedad-posicion',
  dip: 'dip-fcf',
  rem: 'rem',
  ram: 'ram',
  horaPartograma: 'hora-partograma',
  horaReal: 'hora-real',
  columna: 'columna-partograma',
  posicionMaterna: 'posicion-materna',
  contraccionesDuracion: 'contracciones-duracion',
  contraccionesFrecuencia: 'contracciones-frecuencia',
  dolorLocalizacion: 'dolor-localizacion',
  dolorIntensidad: 'dolor-intensidad',
  paridad: 'paridad',
  membranas: 'membranas',
  horaBaseLineaAlerta: 'hora-base-linea-alerta',
} as const;

const FCF = { system: LOINC, code: '55283-6', display: 'Fetal Heart rate' } as const;
const PULSO = { system: LOINC, code: '8867-4', display: 'Heart rate' } as const;
const TA = { system: LOINC, code: '85354-9', display: 'Blood pressure panel with all children optional' } as const;
const SISTOLICA = { system: LOINC, code: '8480-6', display: 'Systolic blood pressure' } as const;
const DIASTOLICA = { system: LOINC, code: '8462-4', display: 'Diastolic blood pressure' } as const;

const DISPLAY: Readonly<Record<string, string>> = {
  [CODIGO_PARTOGRAMA.panel]: 'Partograma',
  [CODIGO_PARTOGRAMA.dilatacion]: 'Dilatación cervical',
  [CODIGO_PARTOGRAMA.hodge]: 'Plano de Hodge',
  [CODIGO_PARTOGRAMA.variedad]: 'Variedad de posición',
  [CODIGO_PARTOGRAMA.dip]: 'Dip de la frecuencia cardíaca fetal',
  [CODIGO_PARTOGRAMA.rem]: 'Ruptura espontánea de membranas (REM)',
  [CODIGO_PARTOGRAMA.ram]: 'Ruptura artificial de membranas (RAM)',
  [CODIGO_PARTOGRAMA.horaPartograma]: 'Hora del partograma',
  [CODIGO_PARTOGRAMA.horaReal]: 'Hora real',
  [CODIGO_PARTOGRAMA.columna]: 'Columna de la tabla horaria',
  [CODIGO_PARTOGRAMA.posicionMaterna]: 'Posición materna',
  [CODIGO_PARTOGRAMA.contraccionesDuracion]: 'Duración de las contracciones',
  [CODIGO_PARTOGRAMA.contraccionesFrecuencia]: 'Frecuencia de las contracciones',
  [CODIGO_PARTOGRAMA.dolorLocalizacion]: 'Localización del dolor',
  [CODIGO_PARTOGRAMA.dolorIntensidad]: 'Intensidad del dolor',
  [CODIGO_PARTOGRAMA.paridad]: 'Paridad (curva de alerta)',
  [CODIGO_PARTOGRAMA.membranas]: 'Membranas (curva de alerta)',
  [CODIGO_PARTOGRAMA.horaBaseLineaAlerta]: 'Hora de la línea de base de la curva de alerta',
};

const DISPLAY_POSICION: Readonly<Record<string, string>> = { LD: 'Lateral derecho', LI: 'Lateral izquierdo', D: 'Dorsal', SS: 'Semisentada', S: 'Sentada', PC: 'Parada o caminando' };
const DISPLAY_LOCALIZACION: Readonly<Record<string, string>> = { SP: 'Suprapúbico', S: 'Sacro', U: 'Contracciones uterinas' };
const DISPLAY_INTENSIDAD: Readonly<Record<string, string>> = { '+++': 'Fuerte', '++': 'Moderada', '+': 'Débil', '-': 'No lo percibe' };
const DISPLAY_DIP: Readonly<Record<string, string>> = { I: 'Tipo I (desaceleración precoz)', II: 'Tipo II (desaceleración tardía)', V: 'Variable', M: 'Meconio' };

function local(codigo: string): { system: string; code: string; display: string } {
  return { system: SISTEMA_PARTOGRAMA, code: codigo, display: DISPLAY[codigo] ?? codigo };
}

const cc = (coding: { system: string; code: string; display?: string }[]) => ({ coding });

/** Número desde el texto del papel ("140", "70,5"); `undefined` si no es un número puro. */
export function numeroDeTexto(texto: string): number | undefined {
  const t = texto.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(t)) return undefined;
  return Number(t);
}

/** "120/80" (o "120-80") → sistólica/diastólica; `undefined` si no tiene esa forma. */
export function leerTensionArterial(texto: string): { sistolica: number; diastolica: number } | undefined {
  const m = /^\s*(\d{2,3})\s*[/-]\s*(\d{2,3})\s*$/.exec(texto);
  return m ? { sistolica: Number(m[1]), diastolica: Number(m[2]) } : undefined;
}

const q = (value: number, unit: string, code: string) => ({ value, unit, system: UCUM, code });
const horas = (h: number) => q(h, 'h', 'h');
const porMinuto = (v: number) => q(v, '/min', '/min');

interface Contexto {
  readonly lote: LoteTransaccional;
  readonly subject: Observation['subject'];
  readonly encounter: Reference<Encounter> | undefined;
  readonly efectivo: string;
  readonly miembros: Reference<Observation>[];
}

function crearMiembro(ctx: Contexto, o: Pick<Observation, 'code'> & Partial<Observation>): Observation {
  const fullUrl = idTemporal();
  const obs = ctx.lote.crear<Observation>({ resourceType: 'Observation', status: 'final', subject: ctx.subject, encounter: ctx.encounter, effectiveDateTime: ctx.efectivo, ...o }, fullUrl);
  ctx.miembros.push({ reference: fullUrl });
  return obs;
}

const compHora = (h: number): ObservationComponent => ({ code: cc([local(CODIGO_PARTOGRAMA.horaPartograma)]), valueQuantity: horas(h) });

/**
 * Agrega al lote los Observations del partograma y su panel. Devuelve el panel, o `undefined` (sin agregar nada) si no hay ningún dato discreto.
 * `ahora` fija el reloj (`effectiveDateTime`) para poder testear.
 */
export function agregarObservacionesPartograma(
  lote: LoteTransaccional,
  subject: Observation['subject'],
  encounter: Reference<Encounter> | undefined,
  visual: EstadoPartogramaVisual051,
  tabla: EstadoTablaHorariaPartograma051,
  ahora: Date = new Date(),
): Observation | undefined {
  const ctx: Contexto = { lote, subject, encounter, efectivo: ahora.toISOString(), miembros: [] };

  // --- Gráfico ---
  const dilataciones = Object.entries(visual.puntos)
    .map(([hora, cm]) => ({ hora: Number(hora), cm }))
    .filter((p) => Number.isFinite(p.hora) && Number.isFinite(p.cm))
    .sort((a, b) => a.hora - b.hora);
  for (const p of dilataciones) crearMiembro(ctx, { code: cc([local(CODIGO_PARTOGRAMA.dilatacion)]), valueQuantity: q(p.cm, 'cm', 'cm'), component: [compHora(p.hora)] });
  for (const p of visual.fcf) crearMiembro(ctx, { code: cc([FCF]), valueQuantity: porMinuto(p.fcf), component: [compHora(p.hora)] });
  for (const p of visual.hodge) {
    const plano = PLANOS_HODGE_PARTOGRAMA.find((h) => h.plano === p.plano);
    if (!plano) continue;
    const variedad = p.variedad?.trim();
    crearMiembro(ctx, {
      code: cc([local(CODIGO_PARTOGRAMA.hodge)]),
      valueCodeableConcept: cc([
        { system: SISTEMA_PARTOGRAMA_PLANO_HODGE, code: plano.plano, display: `Plano ${plano.plano}` },
        { system: SISTEMA_PARTOGRAMA_DE_LEE, code: plano.deLee, display: `De Lee ${plano.deLee}` },
      ]),
      component: [compHora(p.hora), ...(variedad ? [{ code: cc([local(CODIGO_PARTOGRAMA.variedad)]), valueString: variedad }] : [])],
    });
  }
  for (const d of visual.dips) {
    crearMiembro(ctx, { code: cc([local(CODIGO_PARTOGRAMA.dip)]), valueCodeableConcept: cc([{ system: SISTEMA_PARTOGRAMA_DIP, code: d.tipo, display: DISPLAY_DIP[d.tipo] }]), component: [compHora(d.hora)] });
  }
  if (visual.rem !== null) crearMiembro(ctx, { code: cc([local(CODIGO_PARTOGRAMA.rem)]), valueBoolean: true, component: [compHora(visual.rem)] });
  if (visual.ram !== null) crearMiembro(ctx, { code: cc([local(CODIGO_PARTOGRAMA.ram)]), valueBoolean: true, component: [compHora(visual.ram)] });

  // --- Tabla horaria a-p: solo columnas y campos con dato ---
  for (const columna of COLUMNAS_HORARIAS_PARTOGRAMA) agregarFilaHoraria(ctx, columna, tabla[columna]);

  if (ctx.miembros.length === 0) return undefined;

  // La línea de base solo tiene sentido (y solo se dibuja) con paridad y membranas elegidas; el '0' por defecto no se guarda solo.
  const horaBase = visual.paridad && visual.membranas ? numeroDeTexto(visual.horaBaseLineaAlerta) : undefined;
  const componentes: ObservationComponent[] = [
    ...(visual.paridad ? [{ code: cc([local(CODIGO_PARTOGRAMA.paridad)]), valueCodeableConcept: cc([{ system: SISTEMA_PARTOGRAMA_PARIDAD, code: visual.paridad }]) }] : []),
    ...(visual.membranas ? [{ code: cc([local(CODIGO_PARTOGRAMA.membranas)]), valueCodeableConcept: cc([{ system: SISTEMA_PARTOGRAMA_MEMBRANAS, code: visual.membranas }]) }] : []),
    ...(horaBase !== undefined ? [{ code: cc([local(CODIGO_PARTOGRAMA.horaBaseLineaAlerta)]), valueQuantity: horas(horaBase) }] : []),
  ];
  return lote.crear<Observation>({
    resourceType: 'Observation',
    status: 'final',
    code: cc([local(CODIGO_PARTOGRAMA.panel)]),
    subject,
    encounter,
    effectiveDateTime: ctx.efectivo,
    hasMember: ctx.miembros,
    ...(componentes.length > 0 ? { component: componentes } : {}),
  });
}

function agregarFilaHoraria(ctx: Contexto, columna: ColumnaHorariaPartograma, f: EstadoTablaHorariaPartograma051[ColumnaHorariaPartograma]): void {
  const horaReal = f.horaReal.trim();
  const antes = ctx.miembros.length;
  const ident: ObservationComponent[] = [
    { code: cc([local(CODIGO_PARTOGRAMA.columna)]), valueString: columna },
    ...(horaReal ? [{ code: cc([local(CODIGO_PARTOGRAMA.horaReal)]), valueString: horaReal }] : []),
  ];
  // Cada dato: numérico → valueQuantity con unidad UCUM; texto no numérico → valueString tal cual (no se pierde ni se inventa).
  const conNumero = (texto: string, coding: { system: string; code: string; display?: string }, unidad: (n: number) => ReturnType<typeof porMinuto>): void => {
    const t = texto.trim();
    if (!t) return;
    const n = numeroDeTexto(t);
    crearMiembro(ctx, { code: cc([coding]), ...(n === undefined ? { valueString: t } : { valueQuantity: unidad(n) }), component: ident });
  };
  const soloTexto = (texto: string, codigo: string): void => {
    const t = texto.trim();
    if (t) crearMiembro(ctx, { code: cc([local(codigo)]), valueString: t, component: ident });
  };

  const posicion = f.posicionMaterna.trim();
  if (posicion && (POSICIONES_MATERNAS_PARTOGRAMA as readonly string[]).includes(posicion)) {
    crearMiembro(ctx, { code: cc([local(CODIGO_PARTOGRAMA.posicionMaterna)]), valueCodeableConcept: cc([{ system: SISTEMA_PARTOGRAMA_POSICION, code: posicion, display: DISPLAY_POSICION[posicion] }]), component: ident });
  }

  const ta = f.tensionArterial.trim();
  if (ta) {
    const par = leerTensionArterial(ta);
    if (par) {
      crearMiembro(ctx, {
        code: cc([TA]),
        component: [
          ...ident,
          { code: cc([SISTOLICA]), valueQuantity: q(par.sistolica, 'mm[Hg]', 'mm[Hg]') },
          { code: cc([DIASTOLICA]), valueQuantity: q(par.diastolica, 'mm[Hg]', 'mm[Hg]') },
        ],
      });
    } else {
      crearMiembro(ctx, { code: cc([TA]), valueString: ta, component: ident });
    }
  }
  conNumero(f.pulsoMaterno, PULSO, porMinuto);
  conNumero(f.frecuenciaCardiacaFetal, FCF, porMinuto);
  soloTexto(f.duracionContracciones, CODIGO_PARTOGRAMA.contraccionesDuracion);
  soloTexto(f.frecuenciaContracciones, CODIGO_PARTOGRAMA.contraccionesFrecuencia);

  const loc = f.dolorLocalizacion.trim();
  if (loc) crearMiembro(ctx, { code: cc([local(CODIGO_PARTOGRAMA.dolorLocalizacion)]), valueCodeableConcept: cc([{ system: SISTEMA_PARTOGRAMA_DOLOR_LOCALIZACION, code: loc, display: DISPLAY_LOCALIZACION[loc] }]), component: ident });
  const inten = f.dolorIntensidad.trim();
  if (inten) crearMiembro(ctx, { code: cc([local(CODIGO_PARTOGRAMA.dolorIntensidad)]), valueCodeableConcept: cc([{ system: SISTEMA_PARTOGRAMA_DOLOR_INTENSIDAD, code: inten, display: DISPLAY_INTENSIDAD[inten] }]), component: ident });

  // Hora real sola en la columna (sin otro dato): se conserva, porque el PDF la imprime.
  if (horaReal && !ctx.miembros.slice(antes).length) crearMiembro(ctx, { code: cc([local(CODIGO_PARTOGRAMA.horaReal)]), valueString: horaReal, component: [ident[0]!] });
}
